import type { ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UserX } from "lucide-react";
import { useRemovedEmployees } from "@/hooks/useRemovedEmployees";
import { formatDubaiDate } from "@/lib/dubaiDate";
import type { RemovedEmployeeResponse } from "@/types/generated/cms.generated";

interface RemovedEmployeesCardProps {
  organizationId: string;
}

/** Warning emails go out at removal, 7 days before and 1 day before the deletion date. */
const TOTAL_WARNINGS = 3;

const fullName = (e: RemovedEmployeeResponse) =>
  `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() || "—";

const ESTIMATE_HINT = "Switched off before this date; the exact day isn't recorded";

/** Wraps an estimated date's text in a hint tooltip (keyboard-focusable). */
function EstimateHint({ children }: { children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="cursor-help underline decoration-dotted underline-offset-2"
          data-testid="removed-on-estimate-hint"
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{ESTIMATE_HINT}</TooltipContent>
    </Tooltip>
  );
}

/**
 * People switched off before removal dates were recorded carry an estimated date (when the
 * erasure job first saw them), so it reads "before {date}" with a hint — never as the actual
 * removal day.
 */
const removedOnCell = (e: RemovedEmployeeResponse) =>
  e.removedAtIsEstimate ? (
    <EstimateHint>Before {formatDubaiDate(e.removedAt)}</EstimateHint>
  ) : (
    formatDubaiDate(e.removedAt)
  );

const removedOnLine = (e: RemovedEmployeeResponse) =>
  e.removedAtIsEstimate ? (
    <EstimateHint>Switched off before {formatDubaiDate(e.removedAt)}</EstimateHint>
  ) : (
    `Removed on ${formatDubaiDate(e.removedAt)}`
  );

/**
 * Read-only list of an organisation's removed employees (switched off, awaiting erasure).
 * Hidden when nobody is removed. The CMS never reactivates — HR does that in the client portal
 * (or by re-enabling the person in Microsoft Entra).
 */
export function RemovedEmployeesCard({ organizationId }: RemovedEmployeesCardProps) {
  const { data, isError } = useRemovedEmployees(organizationId);
  const removed = data ?? [];

  // A failed fetch must never look like "nobody is removed".
  if (isError) {
    return (
      <Card data-testid="removed-employees-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserX className="h-4 w-4" />
            Removed employees
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive" data-testid="removed-employees-load-error">
            Could not load removed employees. Refresh the page to try again.
          </p>
        </CardContent>
      </Card>
    );
  }
  if (removed.length === 0) return null;

  return (
    <TooltipProvider>
      <Card data-testid="removed-employees-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserX className="h-4 w-4" />
            Removed employees
          </CardTitle>
          <CardDescription>
            Switched off and waiting to be permanently deleted. Their organisation&apos;s HR can
            reactivate them in the client portal until the date shown.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="p-3 text-left">Full Name</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Removed on</th>
                  <th className="p-3 text-left">Deletes on</th>
                  <th className="p-3 text-left">Warnings sent</th>
                </tr>
              </thead>
              <tbody>
                {removed.map((e) => (
                  <RemovedRow key={e.employeeId} employee={e} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3 p-3">
            {removed.map((e) => (
              <div
                key={e.employeeId}
                className="p-4 rounded-lg border space-y-2"
                data-testid="removed-employee-card-mobile"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-medium">{fullName(e)}</h4>
                    <p className="text-xs text-muted-foreground">{e.email}</p>
                  </div>
                  <Badge variant="destructive">Removed</Badge>
                </div>
                <div
                  className="text-sm text-muted-foreground"
                  data-testid="removed-employee-removed-on-mobile"
                >
                  {removedOnLine(e)}
                </div>
                <p className="text-sm">
                  <span className="text-muted-foreground">Deletes on: </span>
                  On or shortly after {formatDubaiDate(e.deletesOn)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Warnings sent: {e.warningsSent} of {TOTAL_WARNINGS}
                </p>
                {e.isEntraManaged && (
                  <p className="text-xs text-muted-foreground">Managed in Microsoft Entra</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

function RemovedRow({ employee: e }: { employee: RemovedEmployeeResponse }) {
  return (
    <tr className="border-b" data-testid="removed-employee-row">
      <td className="p-3">
        <span className="font-medium">{fullName(e)}</span>
        <div className="text-xs text-muted-foreground">{e.email}</div>
        {e.isEntraManaged && (
          <div className="text-xs text-muted-foreground">Managed in Microsoft Entra</div>
        )}
      </td>
      <td className="p-3">
        <Badge variant="destructive" data-testid="removed-employee-badge">
          Removed
        </Badge>
      </td>
      <td className="p-3 text-sm" data-testid="removed-employee-removed-on">
        {removedOnCell(e)}
      </td>
      <td className="p-3 text-sm" data-testid="removed-employee-deletes-on">
        On or shortly after {formatDubaiDate(e.deletesOn)}
      </td>
      <td className="p-3 text-sm" data-testid="removed-employee-warnings">
        {e.warningsSent} of {TOTAL_WARNINGS}
      </td>
    </tr>
  );
}
